import { create } from 'zustand';

import type {
  AiChatClientAction,
  AiChatClientActionRoute,
  AiChatClientActionType,
  AiChatClientFilterKey,
  AiChatClientFilterValue,
  AiChatClientView,
} from '@govard/ai-chat-kit';

/**
 * PR #973 MVP: the browser side of native-chat client actions.
 *
 * The server owns which action a turn produces; this bus only executes it. It
 * deduplicates by durable `eventId`, so a reconnect that replays the ledger, or
 * a duplicated SSE frame, can never make the app navigate twice. Unknown or
 * malformed actions are dropped here instead of being forwarded to the router.
 *
 * The allowlist below is intentionally owned by this module rather than read
 * from the wire contract at runtime: it is the last boundary before
 * `navigate`, so it must hold even when the module that delivered the envelope
 * is stubbed, swapped or out of date. A route added to the contract without a
 * matching entry here simply stays non-executable, which fails closed.
 */

/** In-app sections this client is willing to navigate to. */
export const CHAT_CLIENT_ACTION_ROUTES = [
  'deals',
  'contacts',
  'tasks',
  'meetings',
  'projects',
  'documents',
  'companies',
  'templates',
] as const satisfies readonly AiChatClientActionRoute[];

/**
 * Mirrors the contract's entity-id shape: a single route segment that cannot
 * contain a separator or a relative marker, so a stored id can never become a
 * different path.
 */
const CHAT_CLIENT_ACTION_ENTITY_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,199}$/;

/**
 * Views and filter keys/values this client is willing to drive. Re-declared
 * here rather than imported from the contract for the reason in the header: a
 * stubbed, swapped or older contract module must not be able to widen what
 * reaches `navigate`, and these lists must survive it being unavailable.
 *
 * Each vocabulary is finite and server-owned. Free-text search, user-defined
 * tags and relation ids are deliberately absent: their values are user data, so
 * no client action may carry them.
 */
export const CHAT_CLIENT_ACTION_VIEWS = [
  'kanban',
  'list',
  'cards',
] as const satisfies readonly AiChatClientView[];

export const CHAT_CLIENT_FILTER_KEYS = ['has'] as const satisfies readonly AiChatClientFilterKey[];

export const CHAT_CLIENT_FILTER_VALUES = [
  'deals',
  'email',
] as const satisfies readonly AiChatClientFilterValue[];

/**
 * The session a queued command belongs to. A command is only ever executed
 * while the page still holds exactly this workspace + user, so an action that
 * was produced under a previous scope cannot move the browser after a
 * workspace switch, an account switch or a logout.
 */
export interface ChatClientActionScopeIdentity {
  readonly workspaceId: string;
  readonly userId: string;
}

export interface ChatClientActionCommand {
  /** Durable envelope id — the dedup key across reconnect and replay. */
  readonly eventId: string;
  readonly conversationId: string;
  /** Session that produced the action; verified again before it executes. */
  readonly scope: ChatClientActionScopeIdentity;
  readonly action: AiChatClientAction;
}

export type ChatClientActionStatus =
  | { readonly kind: 'executed'; readonly action: AiChatClientActionType }
  | { readonly kind: 'unsupported' };

const chatClientActionRouteSet = new Set<string>(CHAT_CLIENT_ACTION_ROUTES);
const chatClientActionViewSet = new Set<string>(CHAT_CLIENT_ACTION_VIEWS);
const chatClientActionFilterKeySet = new Set<string>(CHAT_CLIENT_FILTER_KEYS);
const chatClientActionFilterValueSet = new Set<string>(CHAT_CLIENT_FILTER_VALUES);

/** Bounded so a long session cannot grow the dedup set without limit. */
const EXECUTED_EVENT_ID_LIMIT = 200;
const executedEventIds = new Set<string>();
const executedEventIdOrder: string[] = [];

export function sameChatClientActionScope(
  left: ChatClientActionScopeIdentity | null,
  right: ChatClientActionScopeIdentity | null,
): boolean {
  if (left === null || right === null) return false;
  return left.workspaceId === right.workspaceId && left.userId === right.userId;
}

function forgetExecutedEventIds(): void {
  executedEventIds.clear();
  executedEventIdOrder.length = 0;
}

interface ChatClientActionBusState {
  /**
   * Session the queue currently belongs to, or null when the bus is inactive
   * (logged out). Enqueueing is refused while inactive.
   */
  scope: ChatClientActionScopeIdentity | null;
  queue: ChatClientActionCommand[];
  status: ChatClientActionStatus | null;
  activateScope: (scope: ChatClientActionScopeIdentity | null) => void;
  enqueue: (command: ChatClientActionCommand) => void;
  shift: (
    expectedScope: ChatClientActionScopeIdentity | null,
  ) => ChatClientActionCommand | undefined;
  setStatus: (status: ChatClientActionStatus | null) => void;
}

export const useChatClientActionBus = create<ChatClientActionBusState>()((set, get) => ({
  scope: null,
  queue: [],
  status: null,
  activateScope: (scope) => {
    if (sameChatClientActionScope(get().scope, scope)) return;
    // Switching workspace/user, or signing out, invalidates everything the bus
    // is holding: the queue must not survive into the next session, and the
    // dedup memory must not outlive the session that produced those ids.
    forgetExecutedEventIds();
    set({ scope, queue: [], status: null });
  },
  enqueue: (command) => {
    // Fail closed on an unactivated bus and on a command produced under a
    // different session, so a late stream frame cannot queue a stale move.
    if (!sameChatClientActionScope(get().scope, command.scope)) return;
    if (executedEventIds.has(command.eventId)) return;
    executedEventIds.add(command.eventId);
    executedEventIdOrder.push(command.eventId);
    if (executedEventIdOrder.length > EXECUTED_EVENT_ID_LIMIT) {
      const oldest = executedEventIdOrder.shift();
      if (oldest !== undefined) executedEventIds.delete(oldest);
    }
    set({ queue: [...get().queue, command] });
  },
  shift: (expectedScope) => {
    const state = get();
    // The executor re-verifies the live scope here rather than trusting that
    // `activateScope` already ran: a command queued before a scope change is
    // discarded even if the matching effect has not fired yet.
    if (!sameChatClientActionScope(state.scope, expectedScope)) {
      if (state.queue.length > 0 || state.status !== null) {
        forgetExecutedEventIds();
        set({ queue: [], status: null });
      }
      return undefined;
    }
    const [head, ...rest] = state.queue;
    if (head !== undefined) set({ queue: rest });
    return head;
  },
  setStatus: (status) => set({ status }),
}));

/**
 * Non-reactive entry point for the stream consumer: dispatching must not make
 * the chat hook subscribe to the bus, only the router-owning page does.
 */
export function enqueueChatClientAction(command: ChatClientActionCommand): void {
  useChatClientActionBus.getState().enqueue(command);
}

/**
 * Turns a validated action into an in-app path, or nothing.
 *
 * The route vocabulary and the entity-id shape are re-checked here on purpose:
 * the browser must not trust the envelope it received even though the server
 * produced it. There is no URL or href in the contract, so an external target
 * cannot be expressed.
 *
 * `set_filter` and `switch_view` do not invent page state — they address state
 * the target list page already reads out of the URL on its very first render:
 * `DealKanbanPage` reads `view`, `ContactsPage` reads `has`. Every other filter
 * and view in these pages lives in local `useState` that a navigation cannot
 * reach, so those combinations are gaps rather than new plumbing here.
 */
export function resolveInternalChatActionPath(action: AiChatClientAction): string | undefined {
  // `action` is typed, but this is the last boundary before `navigate` and the
  // value arrived from the wire: a nullish action or a nullish `filter` would
  // otherwise throw a TypeError inside the page's effect and unmount the chat
  // view. Every later check is already a `Set.has` on a possibly-garbage value,
  // which simply answers false, so only these two need an explicit guard.
  if (action === null || typeof action !== 'object') return undefined;
  if (!chatClientActionRouteSet.has(action.route)) return undefined;
  if (action.action === 'navigate') return `/${action.route}`;
  if (action.action === 'switch_view') {
    if (action.route !== 'deals' || !chatClientActionViewSet.has(action.view)) return undefined;
    // `URLSearchParams` rather than string interpolation: the vocabulary is
    // closed today, and this keeps the path safe if it is ever widened.
    return `/${action.route}?${new URLSearchParams({ view: action.view })}`;
  }
  if (action.action === 'set_filter') {
    const filter = action.filter;
    if (filter === null || typeof filter !== 'object') return undefined;
    if (
      action.route !== 'contacts' ||
      !chatClientActionFilterKeySet.has(filter.key) ||
      !chatClientActionFilterValueSet.has(filter.value)
    ) {
      return undefined;
    }
    return `/${action.route}?${new URLSearchParams({ [filter.key]: filter.value })}`;
  }
  if (!CHAT_CLIENT_ACTION_ENTITY_ID_PATTERN.test(action.entityId)) return undefined;
  return `/${action.route}/${action.entityId}`;
}

export function isChatClientActionRoute(value: unknown): value is AiChatClientActionRoute {
  return typeof value === 'string' && chatClientActionRouteSet.has(value);
}

/**
 * PR #974 (AI cockpit): local client actions.
 *
 * The assistant's own moves arrive with a server-owned durable `eventId`. A chip
 * the person clicks in the cockpit carries the *same* closed contract, but it is
 * an explicit user choice rather than a model-emitted turn, so it is queued
 * locally and namespaced with this prefix. The prefix lets the executor — and
 * the UI copy — tell a user action apart from an assistant-proposed one, and it
 * keeps a local id from ever colliding with a ledger id that a stream could
 * replay. Local actions are never written to the ledger and are never
 * re-queued after a reload.
 */
export const LOCAL_CHAT_CLIENT_ACTION_EVENT_ID_PREFIX = 'local:';

/** `local:<uuid>` — the dedup identity of one explicit user action. */
export function createLocalChatClientActionEventId(): string {
  const unique =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${LOCAL_CHAT_CLIENT_ACTION_EVENT_ID_PREFIX}${unique}`;
}

export interface LocalChatClientActionInput {
  readonly action: AiChatClientAction;
  /** Conversation the chip belongs to; empty for shell-level cockpit chips. */
  readonly conversationId?: string;
  /**
   * Dedup id for an idempotent chip. Defaults to a fresh `local:<uuid>`; a
   * caller that passes one takes over dedup responsibility.
   */
  readonly eventId?: string;
}

/**
 * Queues an explicit, non-mutating user action (a cockpit chip) through the same
 * bus the assistant uses.
 *
 * Fail closed: a local chip may only act inside the session the bus is bound to
 * right now. Without an active scope (signed out, or the scope still resolving)
 * nothing is queued, and an explicit scope that does not match the live one is
 * dropped rather than trusted. The action is re-validated against the same path
 * allowlist as the wire (see `resolveInternalChatActionPath`), so a chip can
 * never widen what is executable. Returns true only when the command was
 * actually queued (a duplicate id answers false).
 */
export function enqueueLocalChatClientAction(
  input: LocalChatClientActionInput,
  explicitScope?: ChatClientActionScopeIdentity | null,
): boolean {
  const state = useChatClientActionBus.getState();
  const scope = explicitScope === undefined ? state.scope : explicitScope;
  if (scope === null || !sameChatClientActionScope(scope, state.scope)) return false;
  if (resolveInternalChatActionPath(input.action) === undefined) return false;

  const command: ChatClientActionCommand = {
    eventId: input.eventId ?? createLocalChatClientActionEventId(),
    conversationId: input.conversationId ?? '',
    scope,
    action: input.action,
  };
  const queueLengthBefore = state.queue.length;
  state.enqueue(command);
  return useChatClientActionBus.getState().queue.length > queueLengthBefore;
}
